import { useEffect, useId, useState } from 'react'

import Dropdown from 'ageas-react-component-dropdownlist'
import NumberField from 'ageas-react-component-numberfield'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { Result } from '../../../@types/PolicyHolderDetails'
import { useLoadPolicyHolderReferenceDataStore } from '../../../Stores/useLoadPolicyHolderReferenceDataStore'
import { t } from '../../../i18n'
import { validateEmail, validatePhoneNumber } from '../../../utils/validates'

export interface FormContactValuesProps {
  email: string
  contactPhone: string
  contactPhoneNumber: string
  contactDescription: string
}

type Props = {
  hiddenTitle?: boolean
  hiddenEmail?: boolean
  onChange: (value: FormContactValuesProps) => void
  initialFormValues: FormContactValuesProps
  hasUpdate?: boolean
}

export const initialContactFormValues = {
  email: '',
  contactPhone: '',
  contactPhoneNumber: '',
  contactDescription: '',
}

export const MOBILE = '2'

export const ContactForm = ({
  hiddenTitle = false,
  hiddenEmail = false,
  onChange,
  initialFormValues,
  hasUpdate,
}: Props) => {
  const id = useId()
  const [formValues, setFormValues] =
    useState<Partial<FormContactValuesProps>>(initialFormValues)
  const { policyHolderReferenceData } = useLoadPolicyHolderReferenceDataStore()

  const formatLovValues = (values: Result[]) => {
    return values.map((value) => ({
      caption: value?.description,
      value: value?.idElement,
    }))
  }

  const handleChange = (value: any) => {
    setFormValues((prevState) => ({
      ...prevState,
      ...value,
    }))
  }

  const emailMessage = () => {
    const email = formValues?.email || ''
    const emailLength = email.length

    if (emailLength > 0) {
      if (!validateEmail(email)) {
        return t('pages.step3.contacts.invalidEmail')
      }
    } else if (emailLength === 0 && hasUpdate) {
      return t('pages.step3.mandatory')
    }

    return ''
  }

  const phoneNumberMessage = () => {
    const phoneNumber = formValues?.contactPhoneNumber || ''
    const phoneNumberLength = phoneNumber.length

    if (phoneNumberLength > 0) {
      if (phoneNumberLength < 9) {
        return t('pages.step3.contacts.invalidPhoneNumberLength')
      }
      if (
        !validatePhoneNumber(phoneNumber, formValues.contactPhone === MOBILE)
      ) {
        return t('pages.step3.contacts.invalidPhoneNumber')
      }
    } else if (phoneNumberLength === 0 && hasUpdate) {
      return t('pages.step3.mandatory')
    }

    return ''
  }

  const contactTypeMessage = () => {
    const contactPhone = formValues?.contactPhone || ''
    const contactPhoneLength = contactPhone.length

    if (contactPhoneLength === 0 && hasUpdate) {
      return t('pages.step3.mandatory')
    }

    return ''
  }

  useEffect(() => {
    onChange(formValues as never)
  }, [formValues, onChange])

  return (
    <>
      {!hiddenTitle && (
        <Section
          id={`${id}createEntityContactSection`}
          name="Contacto"
          config={{ labelSize: '20px', labelBold: '500' }}
          colorTheme="blue"
        />
      )}
      {!hiddenEmail && (
        <TextField
          required
          id={`${id}email`}
          key={`email-textfield-${hasUpdate}`}
          name="email"
          message={emailMessage()}
          label={t('entity.email')}
          value={formValues.email}
          onValueChanged={(id: string, value: string) =>
            handleChange({ email: value })
          }
          colorTheme="blue"
          maxLength={70}
          version="2"
        />
      )}
      <Dropdown
        required
        id={`${id}type-contact`}
        name="type-contact"
        key={`contact-type-${hasUpdate}`}
        label={t('pages.step3.contacts.phoneContact')}
        message={contactTypeMessage()}
        value={formValues.contactPhone}
        onValueChanged={(id: any, value: any) => {
          handleChange({ contactPhone: value })
          handleChange({
            contactDescription: policyHolderReferenceData?.contactTypes?.find(
              (item) => item.idElement === value,
            )?.description,
          })
        }}
        colorTheme="blue"
        options={formatLovValues(policyHolderReferenceData?.contactTypes || [])}
        version="2"
      />
      <NumberField
        required
        id={`${id}phoneNumber`}
        key={`contact-form-phoneNumber-${hasUpdate}`}
        message={phoneNumberMessage()}
        name="phoneNumber"
        label={t('pages.step3.contacts.phoneNumber')}
        maxLength={9}
        colorTheme="blue"
        mask="000000000"
        version="2"
        value={formValues.contactPhoneNumber}
        onValueChanged={(id: any, value: any) =>
          handleChange({ contactPhoneNumber: value })
        }
      />
    </>
  )
}
